import classNames from 'classnames'
import React from 'react'
import { connect } from 'react-redux'

import { State } from 'admin/redux/ducks'
import {
  showTooltipAction,
  hideTooltipAction,
  TooltipState,
  TooltipModes,
  TooltipPositions,
} from 'redux/ducks/tooltip'

const TooltipTrigger: React.FunctionComponent<TooltipTriggerProps> = props => {
  const { text, mode, position, children, showTooltip, hideTooltip } = props
  const tooltipProps = {
    onMouseEnter: e => showTooltip(text, e.currentTarget, { mode, position }),
    onMouseLeave: hideTooltip,
  }
  // Wrap multiple children
  if (React.Children.count(children) > 1) {
    return <div {...tooltipProps}>{children}</div>
    // Clone single child
  } 
    return React.cloneElement(children as React.ReactElement<any>, tooltipProps)
  
}
type TooltipTriggerProps = {
  text: React.ReactNode,
  mode?: TooltipModes,
  position?: TooltipPositions,
  children: React.ReactElement<any>,
  showTooltip: (text, element, options) => void,
  hideTooltip: () => void,
}

class TooltipView extends React.Component<TooltipViewProps, any> {
  constructor(props) {
    super(props)

    this.state = {
      element: null,
      position: null,
      left: null,
      top: null,
    }
  }

  getCoordinates(
    element: HTMLElement,
    position: TooltipPositions,
  ): { left: string; top: string } {
    const elementBounds = element.getBoundingClientRect()
    let left
    let top
    // IE11 compatibility: pageXOffset/pageYOffset
    const scrollX = window.scrollX || window.pageXOffset
    const scrollY = window.scrollY || window.pageYOffset
    switch (position) {
      case TooltipPositions.EAST:
        left = scrollX + elementBounds.left + elementBounds.width
        top = scrollY + elementBounds.top + elementBounds.height / 2
        break
      case TooltipPositions.WEST:
        left = scrollX + elementBounds.left
        top = scrollY + elementBounds.top + elementBounds.height / 2
        break
      case TooltipPositions.SOUTH:
        left = scrollX + elementBounds.left + elementBounds.width / 2
        top = scrollY + elementBounds.top + elementBounds.height
        break
      case TooltipPositions.NORTH:
      default:
        left = scrollX + elementBounds.left + elementBounds.width / 2
        top = scrollY + elementBounds.top
        break
    }
    return {
      left: `${left  }px`,
      top: `${top  }px`,
    }
  }

  handleScrollResize = () => {
    window.requestAnimationFrame(() => {
      this.setState(state => {
        return this.getCoordinates(state.element, state.position)
      })
    })
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.tooltip && prevProps.tooltip.visible !== this.props.tooltip.visible ||
      this.props.tooltip && prevProps.tooltip.element !== this.props.tooltip.element
    ) {
      if (this.props.tooltip.visible) {
        this.setState(
          {
            element: this.props.tooltip.element,
            position: this.props.tooltip.position,
            ...this.getCoordinates(
              this.props.tooltip.element,
              this.props.tooltip.position,
            ),
          },
          () => {
            window.document
              .querySelector('#main-container')
              .addEventListener('scroll', this.handleScrollResize)
            window.addEventListener('resize', this.handleScrollResize)
          },
        )
      } else {
        window.document
          .querySelector('#main-container')
          .removeEventListener('scroll', this.handleScrollResize)
        window.removeEventListener('resize', this.handleScrollResize)
      }
    }
  }

  render() {
    const { tooltip } = this.props
    const { visible, position, text } =
      tooltip || { visible: null, position: null, text: null}
    const { left, top } = this.state
    const tooltipClasses = classNames(
      'Tooltip',
      !visible && 'Tooltip--hidden',
      `Tooltip--${  position}`,
    )
    return (
      <div style={{ left, top }} className={tooltipClasses}>
        {text}
      </div>
    )
  }
}

type TooltipViewProps = {
  tooltip: TooltipState,
}

const mapStateToProps = (state: State, ownProps) => {
  return {
    tooltip: state.tooltip as TooltipState,
  }
}
const mapDispatchToProps = dispatch => {
  return {
    showTooltip: (text, element, options) =>
      dispatch(showTooltipAction(text, element, options)),
    hideTooltip: () => dispatch(hideTooltipAction()),
  }
}

export default connect(null, mapDispatchToProps)(TooltipTrigger)
export const Tooltip = connect(mapStateToProps)(TooltipView)
